import type { StrategyNode } from '../../../../types/BaseNode';
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';
import { handleNodeDrop } from '../../../../utils/handleDrop';
import { SingleNode } from '../../../../types/SingleNode';
import { CompositeNode } from '../../../../types/CompositeNode';
import { FormatterNode } from '../../../../types/FormatterNode';
import { PnLNode } from '../../../../types/PnLNode';
import { VarianceNode } from '../../../../types/VarianceNode';
import { RegressionNode } from '../../../../types/RegressionNode';
import { findNodeById } from '../../../../utils/treeHandler';
import { v4 as uuid } from 'uuid';

interface Props {
    node: StrategyNode;
}

/**
 * Renders a placeholder card for an empty slot in a Composite or Formatter node.
 * This card acts as a drop zone and allows quick insertion of new nodes.
 */
export function PlaceholderCard({ node }: Props) {
    const { onUpdate, rootNode } = useNodeOperations();
    const { parentId, slot, label: placeholderLabel } = node.config as { parentId: string, slot: string, label?: string };

    const createAlphaBookNode = () =>
        new SingleNode(uuid(), "MeepMeep AlphaBook", false, {
            typeId: 'meepmeep_alphabook',
            props: { market: 'TTF', product: 'DA', broker: 'EEX_SPOT' }
        });

    const isAllowedForSlot = (parent: StrategyNode, newChild: StrategyNode): boolean => {
        if (parent.type === 'MeepMeep' && slot === 'main_market') {
            return newChild.type === 'Single' && newChild.config?.typeId === 'meepmeep_alphabook';
        }

        if (parent.type === 'MeepMeep' && slot === 'signal') {
            return newChild.type === 'Regression';
        }

        if (parent.type === 'Regression') {
            return (
                (newChild.type === 'Single' && newChild.config?.typeId === 'meepmeep_alphabook') ||
                (newChild.type === 'Formatter' && !!newChild.config?.child)
            );
        }

        return true;
    };

    const insertChild = (newChild: StrategyNode) => {
        if (!rootNode) return;
        const parent = findNodeById(rootNode, parentId);
        if (!parent) return;

        if (!isAllowedForSlot(parent, newChild)) {
            if (parent.type === 'MeepMeep' && slot === 'main_market') {
                alert('MeepMeep main market must be a MeepMeep AlphaBook source.');
                return;
            }
            if (parent.type === 'MeepMeep' && slot === 'signal') {
                alert('MeepMeep signal must be a Regression node.');
                return;
            }
            if (parent.type === 'Regression') {
                alert('Regression inputs must be MeepMeep AlphaBook sources or formatter chains rooted in AlphaBook.');
                return;
            }
        }

        if (parent.type === 'Composite') {
            const updated = new CompositeNode(parent.id, parent.title, parent.config.operator, parent.config.combine_on);
            updated.config.left = slot === 'left' ? newChild : parent.config.left;
            updated.config.right = slot === 'right' ? newChild : parent.config.right;
            onUpdate(parentId, updated);
        } else if (parent.type === 'Formatter') {
            const updated = new FormatterNode(parent.id, parent.title);
            updated.config = {
                ...(parent.config as FormatterNode['config']),
                child: newChild
            };
            onUpdate(parentId, updated);
        } else if (parent.type === 'PnL') {
            const updated = new PnLNode(
                parent.id,
                parent.title,
                parent.config.entry,
                parent.config.exit,
                parent.config.maxLots,
                parent.config.lotSize,
                parent.config.fees,
                parent.config.mode
            );
            if (slot === 'entry') updated.config.entry = newChild;
            if (slot === 'exit') updated.config.exit = newChild;
            onUpdate(parentId, updated);
        } else if (parent.type === 'Variance') {
            const updated = new VarianceNode(
                parent.id,
                parent.title,
                parent.config.target,
                [...(parent.config.parameters ?? [])]
            );
            if (slot === 'entry') {
                // entry slot = target
                updated.config.target = newChild;
            } else if (slot === 'right') {
                // right slot = add to parameters (max 3)
                if (updated.config.parameters.length < 3) {
                    updated.config.parameters.push(newChild);
                } else {
                    alert('The Variance node supports at most 3 parameter inputs.');
                    return;
                }
            }
            onUpdate(parentId, updated);
        } else if (parent.type === 'MeepMeep') {
            const updated = new (parent.constructor as any)(
                parent.id, 
                parent.title, 
                slot === 'main_market' ? newChild : parent.config.main_market,
                slot === 'signal' ? newChild : parent.config.signal,
                    parent.config.latency_ms,
                parent.config.insample_days,
                parent.config.target_forward_ticks,
                parent.config.max_volume,
                parent.config.trade_margin,
                parent.isManualRename
            );
            onUpdate(parentId, updated);
        } else if (parent.type === 'Regression') {
            const updatedInputs = [...(parent.config.inputs || [])];
            const slotMatch = slot.match(/input-(\d+)/);
            if (slotMatch) {
                const idx = parseInt(slotMatch[1]);
                if (idx < updatedInputs.length) {
                    updatedInputs[idx] = newChild;
                } else {
                    updatedInputs.push(newChild);
                }
            } else {
                updatedInputs.push(newChild);
            }
            
            const updated = new (parent.constructor as any)(
                parent.id,
                parent.title,
                parent.config.model_type,
                [...parent.config.weights],
                updatedInputs,
                parent.isManualRename
            );
            onUpdate(parentId, updated);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        handleNodeDrop(e, (droppedNode) => {
            if (droppedNode.type === 'PnL' || droppedNode.type === 'Variance') {
                alert(`${droppedNode.type} nodes can only be placed at the root of a strategy, not inside other nodes.`);
                return;
            }
            if (droppedNode.type === 'Regression' && !(parent?.type === 'MeepMeep' && slot === 'signal')) {
                alert('Regression nodes can only be used as the signal child of a MeepMeep root.');
                return;
            }
            insertChild(droppedNode);
        });
    };

    const parent = rootNode ? findNodeById(rootNode, parentId) : null;
    const isRestrictedMeepSlot =
        !!parent &&
        (
            (parent.type === 'MeepMeep' && (slot === 'main_market' || slot === 'signal')) ||
            parent.type === 'Regression'
        );

    return (
        <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            style={{
                padding: '0.5rem',
                border: '2px dashed var(--text-muted)',
                background: 'var(--surface-overlay-faint)',
                backdropFilter: 'blur(4px)',
                borderRadius: 'var(--radius-md)',
                minHeight: '80px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                opacity: 0.6,
                transition: 'all 0.2s ease',
                boxShadow: 'var(--shadow-sm)',
                boxSizing: 'border-box'
            }}
            className="placeholder-card"
        >
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                Drop {placeholderLabel ? placeholderLabel : (slot === 'child' ? 'input' : (slot === 'entry' || slot === 'exit' ? `${slot} signal` : slot))} here
            </span>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                {isRestrictedMeepSlot ? (
                    <>
                        {parent?.type === 'MeepMeep' && slot === 'signal' ? (
                            <button
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--surface-glass)', border: '1px solid var(--surface-glass-border)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={() => insertChild(new RegressionNode(uuid(), 'Alpha Signal'))}
                            >
                                Regression
                            </button>
                        ) : (
                            <button
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--surface-glass)', border: '1px solid var(--surface-glass-border)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={() => insertChild(createAlphaBookNode())}
                            >
                                Data
                            </button>
                        )}
                        {parent?.type === 'Regression' && (
                            <button
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--surface-glass)', border: '1px solid var(--surface-glass-border)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={() => {
                                    const source = createAlphaBookNode();
                                    const formatter = new FormatterNode(uuid(), "Orderbook Gradient");
                                    formatter.config = {
                                        typeId: 'meepmeep_orderbook_gradient',
                                        props: { levels: 1 },
                                        child: source
                                    };
                                    insertChild(formatter);
                                }}
                            >
                                Feature
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <button
                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--surface-glass)', border: '1px solid var(--surface-glass-border)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => insertChild(new SingleNode(uuid(), "New Data"))}
                        >
                            Data
                        </button>
                        <button
                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--surface-glass)', border: '1px solid var(--surface-glass-border)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => insertChild(new CompositeNode(uuid(), "New Combine"))}
                        >
                            Combine
                        </button>
                        <button
                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--surface-glass)', border: '1px solid var(--surface-glass-border)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => insertChild(new FormatterNode(uuid(), "New Transform"))}
                        >
                            Transform
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
